import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMyClips } from "../../lib/api";

/**
 * MyClipsBlock — dashboard right-column widget that aggregates the latest
 * clips across every league the user is in. Lets users discover Twitch /
 * YouTube highlights without having to open each league individually.
 *
 * Behaviour:
 *  • Fetches /api/me/clips on mount.
 *  • Shows the 6 newest clips with title + poster + league badge.
 *  • Each card opens the underlying clip in a new tab.
 *  • Empty state encourages opening a league's CLIPS tab to be the first
 *    to post.
 */
export function MyClipsBlock() {
  const [clips, setClips] = useState([]);
  const [leagueCount, setLeagueCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchMyClips(6)
      .then((data) => {
        if (!alive) return;
        setClips(data?.clips || []);
        setLeagueCount(data?.league_count || 0);
      })
      .catch(() => { /* silent — block hides on error */ })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="glass rounded-3xl p-4" data-testid="dash-my-clips">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-film mr-1" />LATEST CLIPS
        </p>
        {leagueCount > 0 && (
          <span className="text-[9px] tracking-widest text-white/40">
            ACROSS {leagueCount} LEAGUE{leagueCount === 1 ? "" : "S"}
          </span>
        )}
      </div>
      {loading ? (
        <p className="text-[11px] text-white/40 text-center py-4" data-testid="dash-clips-loading">
          Loading clips…
        </p>
      ) : clips.length === 0 ? (
        <p className="text-[11px] text-white/45 leading-snug" data-testid="dash-clips-empty">
          {leagueCount === 0
            ? <>Join a league to see clips here. Browse <Link to="/leagues" className="text-[#14b8a6]">LEAGUES</Link>.</>
            : <>No clips in your leagues yet. Open a league&apos;s <span className="text-[#14b8a6]">CLIPS</span> tab to drop the first one.</>}
        </p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="dash-clips-list">
          {clips.map((c) => (
            <a
              key={c.clip_id}
              href={c.clip_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`dash-clip-${c.clip_id}`}
              className="block p-2.5 rounded-2xl bg-black/25 border border-white/8 hover:border-[#14b8a6]/45 transition-colors"
            >
              <p className="text-[12px] font-bold text-white truncate">{c.title}</p>
              <p className="text-[10px] text-[#14b8a6]/85 truncate mt-0.5">
                <i className="fas fa-trophy mr-1" />
                {c.league_name || "League"}
                {c.league_game && (
                  <span className="text-white/35 ml-1">· {c.league_game}</span>
                )}
              </p>
              <p className="text-[10px] text-white/45 truncate mt-0.5">
                <i className="fas fa-user mr-1" />{c.poster_name}
              </p>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyClipsBlock;
